import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { useEnvVarMutations } from '@/api/hooks';
import type { EnvVar } from '@/api/schemas';
import { showErrorToast } from '@/lib/toasts';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

/** Soft delete (`DELETE /env-vars/{id}` with its ETag); failures surface as a toast. */
function useDeleteEnvVar(deleting: EnvVar | null, onClose: () => void) {
  const { remove } = useEnvVarMutations();
  return async () => {
    if (!deleting) return;
    try {
      await remove.mutateAsync({ id: deleting.id, etag: deleting.etag });
      toast.success(`${deleting.key} deleted`, { description: 'Existing build snapshots keep their versions.' });
      onClose();
    } catch (e) {
      showErrorToast(e instanceof ApiError ? { title: e.toastTitle, description: `${e.code} · ${e.message}` } : { title: 'Could not delete' });
    }
  };
}

export function DeleteEnvVarDialog({ deleting, onClose }: { deleting: EnvVar | null; onClose: () => void }) {
  const onDelete = useDeleteEnvVar(deleting, onClose);
  return (
    <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => (o ? undefined : onClose())}>
      <AlertDialogContent data-testid="env-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {deleting?.key}?</AlertDialogTitle>
          <AlertDialogDescription>Soft delete: new builds stop seeing it, existing snapshots keep referencing their versions (redeploys with the same snapshot still work).</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="env-delete-dismiss">Keep</AlertDialogCancel>
          <AlertDialogAction
            destructive
            data-testid="env-delete-confirm"
            onClick={(e) => {
              e.preventDefault();
              void onDelete();
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
